import app from 'flarum/forum/app';
import extractText from 'flarum/common/utils/extractText';
import { typingTagIds } from './typingCards';
// zones.ts imports only `app` at runtime, so this is not a cycle.
import { renderZones } from './zones';

// Mithril is global in Flarum.

/**
 * Data-driven widget components (Stats / Online / Top Contributors / Trending).
 * All four share ONE cached request to /api/bespoke/widget-data, so a page full
 * of widgets makes a single round-trip. Each component shows a skeleton until
 * the data arrives, then redraws.
 */

// Client-side fetch cache with a TTL. Identical widgets share one in-flight
// request (and its result) per param combo; differently-configured ones each
// get their own. The TTL means a long-open tab eventually re-fetches instead of
// showing hours-old data — the server still caps the real work at its own 60s
// cache, so re-fetches after the TTL are cheap.
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function cached(map, key, fetch) {
  const hit = map.get(key);
  if (hit && Date.now() - hit.at < CACHE_TTL) return hit.p;
  const p = fetch();
  map.set(key, {
    at: Date.now(),
    p
  });
  return p;
}
const dataCache = new Map();
function widgetData(params = {}) {
  const qs = Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
  return cached(dataCache, qs, () => app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/bespoke/widget-data' + qs
  }).then(r => r || {}).catch(err => {
    // Keep the silent-empty UI, but make the failure discoverable (network
    // error, 403 from a proxy, server error). Mirrors the PHP-side logging.
    console.warn('[bespoke] widget-data fetch failed', err);
    return {};
  }));
}
function fmt(n) {
  if (n == null) return '0';
  return n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k' : String(n);
}
function base() {
  return app.forum.attribute('baseUrl');
}
/**
 * The user's profile through Flarum's own route and slug, so it follows the
 * forum's slug driver. Falls back to the username for a widget payload cached
 * before the slug was added (it expires within a minute).
 */
function profileUrl(u) {
  return app.route('user', {
    username: u.slug || u.username
  });
}
function avatar(u) {
  if (u.avatarUrl) {
    return m('span.Avatar', {
      style: {
        backgroundImage: `url("${u.avatarUrl}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    });
  }
  const letter = String(u.displayName || u.username || '?').charAt(0).toUpperCase();
  // Flex + line-height:1 + an explicit font-size so the initial sits dead-centre.
  // Core's .Avatar carries a line-height tuned for big avatars, which left the
  // letter off-centre in these small (30–32px) widget avatars.
  return m('span.Avatar', {
    style: {
      background: 'var(--bespoke-color-brand)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: '1',
      fontSize: '14px',
      color: '#fff',
      fontWeight: '600'
    }
  }, letter);
}

/** Load shared data (with optional per-widget params from settings) into state. */
function load(self, settings, params, pick) {
  self.bd = undefined;
  widgetData(params(settings || {})).then(d => {
    self.bd = pick(d);
    m.redraw();
  });
}
const skeleton = () => m('.Bespoke-skel', m('.Bespoke-skel-bar'), m('.Bespoke-skel-bar'));
export const StatsWidget = {
  oninit(v) {
    load(this, v.attrs.settings, () => ({}), d => d.stats);
  },
  view(v) {
    const s = this.bd;
    return m('.Bespoke-w.Bespoke-w-stats', [m('h4', v.attrs.settings.title || app.translator.trans('ernestdefoe-bespoke.forum.widget_default_stats_title')), !s ? skeleton() : m('.Bespoke-statGrid', [['discussions', 'widget_stat_discussions'], ['posts', 'widget_stat_posts'], ['users', 'widget_stat_members'], ['online', 'widget_stat_online']].map(([k, label]) => m('.Bespoke-stat', [m('b', fmt(s[k])), m('span', app.translator.trans('ernestdefoe-bespoke.forum.' + label))])))]);
  }
};
export const OnlineWidget = {
  oninit(v) {
    load(this, v.attrs.settings, () => ({}), d => ({
      list: d.online,
      visitors: d.visitors
    }));
  },
  view(v) {
    const list = this.bd?.list;
    const visitors = Number(this.bd?.visitors) || 0;
    const showVisitors = v.attrs.settings.showVisitors !== false;
    return m('.Bespoke-w.Bespoke-w-online', [m('h4', [v.attrs.settings.title || app.translator.trans('ernestdefoe-bespoke.forum.widget_default_online_title'), list ? m('span.Bespoke-pill', list.length) : null]), !list ? skeleton() : list.length ? m('.Bespoke-avatars', list.map(u => m('a', {
      href: profileUrl(u),
      title: u.displayName
    }, avatar(u)))) : m('p.Bespoke-w-empty', app.translator.trans('ernestdefoe-bespoke.forum.widget_online_none')), list && showVisitors && visitors > 0 ? m('p.Bespoke-onlineVisitors', app.translator.trans('ernestdefoe-bespoke.forum.widget_online_visitors', {
      count: visitors
    })) : null]);
  }
};
export const ContributorsWidget = {
  oninit(v) {
    load(this, v.attrs.settings, s => ({
      metric: s.metric || 'posts',
      period: s.period || 'all'
    }), d => d.contributors);
  },
  view(v) {
    const list = this.bd;
    const max = Number(v.attrs.settings.count) || 5;
    const unit = {
      posts: 'posts',
      discussions: 'discussions',
      likes: 'likes'
    }[v.attrs.settings.metric || 'posts'] || 'posts';
    const unitKey = 'ernestdefoe-bespoke.forum.widget_contrib_' + unit;
    return m('.Bespoke-w.Bespoke-w-contributors', [m('h4', v.attrs.settings.title || app.translator.trans('ernestdefoe-bespoke.forum.widget_default_contributors_title')), !list ? skeleton() : m('.Bespoke-contribList', list.slice(0, max).map((u, i) => m('a.Bespoke-contribRow', {
      href: profileUrl(u)
    }, [m('span.Bespoke-rank', i + 1), avatar(u), m('span.Bespoke-contribName', u.displayName), u.count != null ? m('span.Bespoke-contribCount', app.translator.trans(unitKey, {
      count: Number(u.count),
      value: fmt(u.count)
    })) : null])))]);
  }
};

// Compact "joined 3 days ago" using the browser's Intl, falling back gracefully.
function ago(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const mins = Math.round(diff / 60000);
    const rtf = new Intl.RelativeTimeFormat(undefined, {
      numeric: 'auto'
    });
    if (mins < 60) return rtf.format(-mins, 'minute');
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return rtf.format(-hrs, 'hour');
    const days = Math.round(hrs / 24);
    if (days < 30) return rtf.format(-days, 'day');
    const months = Math.round(days / 30);
    if (months < 12) return rtf.format(-months, 'month');
    return rtf.format(-Math.round(months / 12), 'year');
  } catch (e) {
    return '';
  }
}
export const NewestMemberWidget = {
  oninit(v) {
    load(this, v.attrs.settings, () => ({}), d => d.newest);
  },
  view(v) {
    const list = this.bd;
    const max = Number(v.attrs.settings.count) || 5;
    const layout = v.attrs.settings.layout || 'list';
    return m('.Bespoke-w.Bespoke-w-newest', [m('h4', v.attrs.settings.title || app.translator.trans('ernestdefoe-bespoke.forum.widget_default_newest_title')), !list ? skeleton() : !list.length ? m('p.Bespoke-w-empty', app.translator.trans('ernestdefoe-bespoke.forum.widget_newest_none')) : layout === 'grid' ? m('.Bespoke-avatars', list.slice(0, max).map(u => m('a', {
      href: profileUrl(u),
      title: u.displayName
    }, avatar(u)))) : m('.Bespoke-newestList', list.slice(0, max).map(u => m('a.Bespoke-newestRow', {
      href: profileUrl(u)
    }, [avatar(u), m('span.Bespoke-newestMain', [m('b', u.displayName), u.joinedAt ? m('span', app.translator.trans('ernestdefoe-bespoke.forum.widget_newest_joined', {
      ago: ago(u.joinedAt)
    })) : null])])))]);
  }
};
export const TrendingWidget = {
  oninit(v) {
    load(this, v.attrs.settings, s => {
      const p = {
        window: s.window || '7d'
      };
      if (s.tag) p.tag = String(s.tag).trim(); // only when set, to avoid cache fragmentation
      return p;
    }, d => d.trending);
  },
  view(v) {
    const list = this.bd;
    const max = Number(v.attrs.settings.count) || 5;
    return m('.Bespoke-w.Bespoke-w-trending', [m('h4', v.attrs.settings.title || app.translator.trans('ernestdefoe-bespoke.forum.widget_default_trending_title')), !list ? skeleton() : m('.Bespoke-trendList', list.slice(0, max).map(d => m('a.Bespoke-trendRow', {
      href: base() + '/d/' + d.id
    }, [m('span.Bespoke-spark', '▲'), m('span.Bespoke-trendMain', [m('b', d.title), m('span', app.translator.trans('ernestdefoe-bespoke.forum.widget_trending_replies', {
      count: Number(d.replies) || 0,
      value: String(d.replies)
    }))])])))]);
  }
};

// ---- Tags list ----
// Renders the forum's tags as colored chips (cloud) or rows (list). Tags are
// normally preloaded in the forum payload by flarum/tags; if the store is
// empty (some payload configurations only preload primary tags) we fetch the
// full list ONCE, module-wide, and redraw.
let tagsRequested = false;
function storeTags() {
  let tags = [];
  try {
    tags = app.store.all('tags') || [];
  } catch (e) {
    /* store not ready */
  }
  if (!tags.length && !tagsRequested) {
    tagsRequested = true;
    try {
      app.store.find('tags').then(() => m.redraw()).catch(() => {});
    } catch (e) {}
  }
  return tags;
}

/** Safe model-method read — tag models proxy attributes through methods. */
function tagAttr(tag, method) {
  try {
    return typeof tag[method] === 'function' ? tag[method]() : undefined;
  } catch (e) {
    return undefined;
  }
}

// The index preloads only the TOP-LEVEL tags, so `children()` is false there
// and a category renders with no subforums. storeTags()'s own fetch guard does
// not help: the store is not empty, it is merely incomplete. The forum-category
// style therefore asks for the full set once, which is what the tags page does.
let allTagsRequested = false;
function ensureAllTags() {
  if (allTagsRequested) return;
  allTagsRequested = true;
  try {
    app.store.find('tags', {
      include: 'lastPostedDiscussion,lastPostedDiscussion.lastPostedUser'
    }).then(() => {
      // renderZones, NOT m.redraw: widgets are mounted with m.render() into
      // their own roots, so a global redraw never reaches them and the
      // subforums would stay missing until the next full page load.
      try {
        renderZones();
      } catch (e) {
        m.redraw();
      }
    }).catch(() => {});
  } catch (e) {
    /* store not ready */
  }
}

/** Children of a tag, taken from the store so a missing relation link is survivable. */
function childTags(parent) {
  const viaRelation = tagAttr(parent, 'children');
  if (Array.isArray(viaRelation) && viaRelation.length) return viaRelation.filter(Boolean);
  const pid = String(tagAttr(parent, 'id') ?? (parent && parent.id ? parent.id() : ''));
  if (!pid) return [];
  return storeTags().filter(t => {
    const p = tagAttr(t, 'parent');
    return p && typeof p.id === 'function' && String(p.id()) === pid;
  });
}
export const TagsListWidget = {
  view(v) {
    const s = v.attrs.settings || {};
    const style = s.style || 'chips';
    const countSetting = Number(s.count);
    const max = countSetting > 0 ? countSetting : Infinity;
    const showCounts = s.showCounts !== false;
    const topOnly = s.topOnly !== false;

    // Forum categories need the child tags, which the index does not preload.
    if (style === 'forums') ensureAllTags();

    // NB: isHidden means "this tag's discussions stay off All Discussions",
    // NOT "hide the tag from listings" — core's tags page shows them, so the
    // widget does too.
    let tags = storeTags().filter(t => {
      if (topOnly && tagAttr(t, 'isChild')) return false;
      return !!tagAttr(t, 'slug');
    });

    // Positioned (primary) tags first in their configured order, then the rest
    // by activity — mirrors how the tag sidebar/tags page ranks them.
    const pos = t => {
      const p = tagAttr(t, 'position');
      return p == null ? Number.MAX_SAFE_INTEGER : p;
    };
    const cnt = t => Number(tagAttr(t, 'discussionCount')) || 0;
    tags.sort((a, b) => pos(a) - pos(b) || cnt(b) - cnt(a));
    tags = tags.slice(0, max);
    const empty = !tags.length;
    const activeTagIds = typingTagIds();
    const typingDots = t => t.id && activeTagIds.has(String(t.id())) ? m('span.Bespoke-tagTyping', {
      title: extractText(app.translator.trans('ernestdefoe-bespoke.forum.tag_typing'))
    }, [m('i'), m('i'), m('i')]) : null;
    const row = t => {
      const color = tagAttr(t, 'color');
      const icon = tagAttr(t, 'icon');
      const href = base() + '/t/' + tagAttr(t, 'slug');
      const name = tagAttr(t, 'name');

      // "Forum categories" — an IPS-style forum row: icon, name, blurb, the
      // child tags as subforum links, and the stats on the right. Deliberately
      // NOT an <a> wrapper: the subforum links and the last-post link have to
      // be real links, and links cannot nest.
      if (style === 'forums') {
        const desc = tagAttr(t, 'description');
        const kids = childTags(t);
        const last = tagAttr(t, 'lastPostedDiscussion');
        return m('.Bespoke-forumCat', {
          style: color ? {
            '--w-tag': color
          } : undefined
        }, [
        // Icon AND name sit in the coloured strip, as in an IPS category box.
        m('.Bespoke-forumCatHead', [icon ? m('span.Bespoke-forumCatIcon', m('i', {
          className: 'icon ' + icon
        })) : null, m('a.Bespoke-forumCatName', {
          href
        }, name), typingDots(t)]), m('.Bespoke-forumCatBody', [desc ? m('p.Bespoke-forumCatDesc', desc) : null, kids.length ? m('.Bespoke-forumCatKids', kids.map(k => {
          const kc = tagAttr(k, 'color');
          const ki = tagAttr(k, 'icon');
          return m('a.Bespoke-forumCatKid', {
            href: base() + '/t/' + tagAttr(k, 'slug'),
            style: kc ? {
              '--w-tag': kc
            } : undefined
          }, [ki ? m('i', {
            className: 'icon ' + ki
          }) : null, m('span', tagAttr(k, 'name'))]);
        })) : null]), m('.Bespoke-forumCatMeta', [showCounts ? m('span.Bespoke-forumCatCount', [m('strong', fmt(cnt(t)))]) : null, last ? m('a.Bespoke-forumCatLast', {
          href: base() + '/d/' + tagAttr(last, 'slug')
        }, tagAttr(last, 'title')) : null])]);
      }
      if (style === 'list') {
        return m('a.Bespoke-tagRow', {
          href,
          style: color ? {
            '--w-tag': color
          } : undefined
        }, [icon ? m('i.Bespoke-tagRowIcon', {
          className: 'icon ' + icon
        }) : m('span.Bespoke-tagRowDot'), m('span.Bespoke-tagRowName', name), typingDots(t), showCounts ? m('span.Bespoke-tagRowCount', fmt(cnt(t))) : null]);
      }
      return m('a.Bespoke-tagChip', {
        href,
        className: color ? 'has-color' : '',
        style: color ? {
          '--w-tag': color
        } : undefined
      }, [icon ? m('i', {
        className: 'icon ' + icon
      }) : null, m('span.Bespoke-tagChipName', name), typingDots(t), showCounts ? m('span.Bespoke-tagChipCount', fmt(cnt(t))) : null]);
    };
    return m('.Bespoke-w.Bespoke-w-tagsList', [s.title ? m('h4', s.title) : null, empty ? m('p.Bespoke-w-empty', app.translator.trans('ernestdefoe-bespoke.forum.widget_tags_empty')) : m(style === 'forums' ? '.Bespoke-forumCats' : style === 'list' ? '.Bespoke-tagRows' : '.Bespoke-tagChips', tags.map(row))]);
  }
};

// ---- Articles (ernestdefoe/page-builder integration) ----
// Pulls published records of the `articles` content type from page-builder's
// public records API, mirroring page-builder's own list rendering: the cover is
// the first show-in-list `image` field and the excerpt is the first show-in-list
// text field, so it stays consistent with the CMS. Registered only when
// page-builder is installed (see widgets/index.ts).
const articlesCache = new Map();
function articlesData(perPage) {
  return cached(articlesCache, String(perPage), () => app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/pagebuilder/content/articles/records?per_page=' + perPage,
    // A missing "articles" content type is a perfectly normal 404 → swallow it
    // quietly. Returning undefined suppresses Flarum's default error handler
    // (console log + alert); any OTHER status returns false so it still
    // surfaces through Flarum's standard handling.
    errorHandler: e => e.status === 404 ? undefined : false
  }).then(r => r || {}).catch(() => ({})) // 404 / network error → render the empty state
  );
}

/** Resolve an image field value (string URL or {url,thumbnailUrl}) to a URL. */
function articleImg(value) {
  if (!value) return null;
  if (typeof value === 'string') return value || null;
  return value.thumbnailUrl || value.url || null;
}

/** Plain-text excerpt from a (possibly HTML/markdown) field value. */
function articleExcerpt(value, len = 140) {
  if (typeof value !== 'string' || !value) return '';
  // DOMParser builds an inert document: unlike innerHTML on a live element,
  // an <img onerror> in the source never loads or runs.
  const text = (new DOMParser().parseFromString(value, 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim();
  return text.length > len ? text.slice(0, len).trim() + '…' : text;
}
export const ArticlesWidget = {
  oninit(v) {
    const count = Math.max(1, Number(v.attrs.settings.count) || 3);
    const filter = String(v.attrs.settings.category || '').trim();
    // With a category filter we over-fetch then filter client-side (the records
    // API filters by numeric id, not slug); otherwise just fetch what we show.
    const perPage = Math.min(50, filter ? 50 : count);
    this.bd = undefined;
    articlesData(perPage).then(data => {
      this.bd = data;
      m.redraw();
    });
  },
  view(v) {
    const s = v.attrs.settings || {};
    const title = s.title || extractText(app.translator.trans('ernestdefoe-bespoke.forum.widget_default_articles_title'));
    const d = this.bd;
    if (!d) return m('.Bespoke-w.Bespoke-w-articles', [m('h4', title), skeleton()]);
    const type = d.type;
    const filter = String(s.category || '').trim();
    let records = d.data || [];
    if (filter) records = records.filter(r => r.category && r.category.slug === filter);
    records = records.slice(0, Math.max(1, Number(s.count) || 3));
    if (!records.length) {
      return m('.Bespoke-w.Bespoke-w-articles', [m('h4', title), m('p.Bespoke-w-empty', app.translator.trans('ernestdefoe-bespoke.forum.widget_articles_none'))]);
    }
    const coverField = type && (type.fields || []).find(f => f.type === 'image' && f.show_in_list);
    const textFields = type ? (type.fields || []).filter(f => f.show_in_list && f !== coverField) : [];
    // Page Builder 3.10.6+ sends each record's resolved cover and excerpt, whatever
    // the type's list settings (its seeded Articles type lists neither field).
    // Older versions only send list fields, so fall back to reading those.
    const resolved = records.some(r => 'cover' in r || 'excerpt' in r);
    const showCover = s.showCover !== false && (resolved || !!coverField);
    const showExcerpt = s.showExcerpt !== false && (resolved || textFields.length > 0);
    const style = s.style || 'cards';
    const slugBase = base() + '/c/' + (type && type.slug || 'articles') + '/';
    const cover = (r, cls) => {
      if (!showCover) return null;
      const url = resolved ? r.cover : articleImg(r.data && r.data[coverField.key]);
      return m('a' + cls + (url ? '' : '.is-empty'), {
        href: slugBase + r.slug,
        style: url ? {
          backgroundImage: `url("${String(url).replace(/"/g, '%22')}")`
        } : undefined
      });
    };
    const excerpt = r => {
      if (!showExcerpt) return null;
      const ex = resolved ? articleExcerpt(r.excerpt) : articleExcerpt(r.data && r.data[textFields[0].key]);
      return ex ? m('p.Bespoke-articleExcerpt', ex) : null;
    };
    const meta = r => m('.Bespoke-articleMeta', [r.category ? m('span.Bespoke-articleCat', r.category.name) : null, r.published_at ? m('span', new Date(r.published_at).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    })) : null]);
    const titleLink = r => m('a.Bespoke-articleTitle', {
      href: slugBase + r.slug
    }, r.title);

    // Full card (cards + carousel): cover on top, body below.
    const card = r => m('.Bespoke-articleCard', [cover(r, '.Bespoke-articleCover'), m('.Bespoke-articleBody', [titleLink(r), excerpt(r), meta(r)])]);
    // Compact row (list + featured tail): small thumb beside the body.
    const row = r => m('.Bespoke-articleRow', [cover(r, '.Bespoke-articleThumb'), m('.Bespoke-articleBody', [titleLink(r), excerpt(r), meta(r)])]);
    let body;
    if (style === 'list') {
      body = m('.Bespoke-articleList', records.map(row));
    } else if (style === 'featured') {
      const [first, ...rest] = records;
      body = [m('.Bespoke-articleFeatured', card(first)), rest.length ? m('.Bespoke-articleList', rest.map(row)) : null];
    } else if (style === 'carousel') {
      body = m('.Bespoke-articleCarousel', records.map(card));
    } else {
      body = m('.Bespoke-articleGrid', records.map(card));
    }
    return m('.Bespoke-w.Bespoke-w-articles', [m('h4', title), body]);
  }
};